// Agency: Offices — ch.06: "Office portfolio and per-office summary … Separate
// tenant records and authorised agency access. Seeded portfolio cards are
// fictional; implement actual tenant selection rather than reusing one office's
// records."
//
// That last clause is the whole brief, and it is a warning about a specific
// failure: the demo draws four cards from one office's data with different
// names on them. So every number on this screen is read from the office whose
// card it is on, in that office's own verified context, and an office that
// could not be read shows what went wrong instead of a plausible figure.
//
// ---------------------------------------------------------------------------
// Two requests per office, and no cross-office endpoint
// ---------------------------------------------------------------------------
//
// GET /administrations and GET /work, per office. That is 2N requests for N
// offices, which is worth saying out loud — and it is the same trade the
// Administrations screen already makes for its per-administration work counts,
// for the same reason: there is no endpoint that groups either by tenant, and
// the alternative would be a number nobody could reproduce.
//
// It is also the thing that makes this screen's isolation claim simple. Every
// figure here came through `tenantRoute()` for exactly one office, so it
// carries the same guarantees any office screen's does: app.begin_request
// verified a live membership or the request 404'd, and row-level security
// decided which administrations of that office count. There is nothing here a
// cross-office query could have got wrong, because there is no cross-office
// query. See apps/web/src/agency.ts.
//
// ---------------------------------------------------------------------------
// What is deliberately not on the card
// ---------------------------------------------------------------------------
//
// No progress bar and no onboarding percentage: those belong to the Onboarding
// screen, which shows the milestone RECORDS, and a bar here would be a
// denominator this screen invented. No revenue, no savings and no ROI —
// ch.06 removed the customer Results tab and V01 forbids the route; an agency
// portfolio full of euro figures would put it back under a different heading.

import { useCallback, type ReactNode } from 'react';
import { request, tenantPath } from '../api/client.ts';
import type { Administration, Membership, WorkQueue } from '../api/types.ts';
import { agencyMemberships, useOfficeReads, type OfficeResult } from '../agency.ts';
import type { ScreenId } from '../components/Shell.tsx';
import { useWorkspace } from '../state/workspace.tsx';

/** What one office contributed: its administrations and its open work. */
interface OfficeSummary {
  readonly administrations: readonly Administration[];
  readonly work: WorkQueue['counts'];
}

export interface AgencyOfficesScreenProps {
  /** Opens one office in the agency workspace, without changing the header. */
  readonly onOpenOffice: (tenantId: string) => void;
  readonly onNavigate: (screen: ScreenId) => void;
}

export function AgencyOfficesScreen({
  onOpenOffice,
  onNavigate,
}: AgencyOfficesScreenProps): ReactNode {
  const workspace = useWorkspace();
  const { token } = workspace;
  const offices = agencyMemberships(workspace.me);

  const read = useCallback(
    async (membership: Membership): Promise<OfficeSummary> => {
      const administrations = await request<{ administrations: Administration[] }>(
        tenantPath(membership.tenantId, '/administrations'),
        { token },
      );
      const work = await request<WorkQueue>(tenantPath(membership.tenantId, '/work'), { token });
      return { administrations: administrations.administrations, work: work.counts };
    },
    [token],
  );

  const { results, busy, reload } = useOfficeReads(offices, read);

  if (offices.length === 0) return <NoPortfolio />;

  const answered = (results ?? []).filter(
    (entry): entry is OfficeResult<OfficeSummary> & { value: OfficeSummary } => entry.value !== null,
  );
  const unreadable = (results ?? []).filter((entry) => entry.value === null);

  const administrations = answered.reduce(
    (total, entry) => total + entry.value.administrations.length,
    0,
  );
  const attention = answered.reduce(
    (total, entry) =>
      total +
      entry.value.administrations.filter(
        (administration) =>
          administration.connection === null || administration.connection.health !== 'healthy',
      ).length,
    0,
  );
  const open = answered.reduce((total, entry) => total + sum(entry.value.work), 0);

  return (
    <>
      <div className="heading">
        <h1>Offices</h1>
        <p>
          The offices you operate. Every figure below was read from that office, under the grants
          that office gave you — not from one office's records with other names on them.
        </p>
      </div>

      <div className="metrics">
        <Metric
          label="Offices"
          value={offices.length}
          caption={
            unreadable.length === 0
              ? 'Where you hold an agency operator membership'
              : `${String(unreadable.length)} could not be read`
          }
        />
        <Metric
          label="Administrations"
          value={administrations}
          caption="Across the offices that answered"
        />
        <Metric label="Need attention" value={attention} caption="No connection, or an unhealthy one" />
        <Metric label="Open work" value={open} caption="Waiting for a person somewhere" />
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <h2>Your portfolio</h2>
            <p>One row per office, read in that office's own context</p>
          </div>
          <button type="button" className="btn" disabled={busy} onClick={reload}>
            {busy ? 'Reading…' : 'Refresh'}
          </button>
        </div>

        {results === null ? (
          <div className="empty">
            <h3>Reading your offices</h3>
            <p>One request per office, in turn.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Office</th>
                  <th>Administrations</th>
                  <th>Connections</th>
                  <th>Open work</th>
                  <th>Your reach</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {results.map((entry) => (
                  <OfficeRow
                    key={entry.membership.tenantId}
                    entry={entry}
                    onOpen={() => {
                      onOpenOffice(entry.membership.tenantId);
                      onNavigate('agency-workspace');
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="card-foot">
          An office appears here because the control plane gave you an agency operator membership in
          it. You cannot add one from this screen, and neither can the office: memberships are
          provisioned, not granted sideways.
        </div>
      </div>

      <div className="card section-space">
        <div className="card-head">
          <h2>What this portfolio does not show</h2>
        </div>
        <div className="card-body">
          <ul className="note-list">
            <li>
              <b>No onboarding progress bar.</b> The milestone records are on the Onboarding screen.
              A percentage here would need a denominator nobody has agreed, and ch.06 is explicit
              that a milestone display never grants execution permission.
            </li>
            <li>
              <b>No revenue, savings or ROI.</b> ch.06 removed the customer Results tab and its
              calculations. Costs &amp; usage is an internal operational view and says so; it is not
              that tab under another heading.
            </li>
            <li>
              <b>Only the administrations you were granted.</b> ch.02: agency support has no
              standing unrestricted access. An office you operate may hold administrations you have
              not been let into, and those are absent from these counts rather than hidden behind a
              refusal.
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}

function OfficeRow({
  entry,
  onOpen,
}: {
  readonly entry: OfficeResult<OfficeSummary>;
  readonly onOpen: () => void;
}): ReactNode {
  const { membership, value, error } = entry;
  const connected =
    value === null
      ? 0
      : value.administrations.filter(
          (administration) => administration.connection?.health === 'healthy',
        ).length;

  return (
    <tr data-testid={`office-${membership.tenantId}`}>
      <td>
        <div className="company">
          <div className="avatar">{membership.tenantName.slice(0, 2).toUpperCase()}</div>
          <div>
            <b>{membership.tenantName}</b>
            <small>{membership.role.replace('_', ' ')}</small>
          </div>
        </div>
      </td>
      {value === null ? (
        // One cell, spanning the figures. Not four cells of dashes: a row of
        // placeholders reads as data at a glance, and this office has none.
        <td colSpan={4}>
          <span className="badge warn">Not read</span> <small>{error}</small>
        </td>
      ) : (
        <>
          <td>{value.administrations.length}</td>
          <td>
            {value.administrations.length === 0 ? (
              <span className="muted">—</span>
            ) : connected === value.administrations.length ? (
              <span className="badge ok">All connected</span>
            ) : (
              <span className="badge warn">
                {connected} of {value.administrations.length} healthy
              </span>
            )}
          </td>
          <td>
            {sum(value.work) === 0 ? (
              <span className="muted">Up to date</span>
            ) : (
              `${String(sum(value.work))} waiting`
            )}
          </td>
          <td>
            {/* The same distinction GET /me draws, because the same number means
                two different things: an owner's empty grant list is not an empty
                reach, and an operator's is exactly their reach. */}
            <small>
              {membership.administrationAccess === 'all'
                ? 'Every administration in this office'
                : `${String(membership.grantedAdministrationIds.length)} granted`}
            </small>
          </td>
        </>
      )}
      <td>
        <button type="button" className="btn" onClick={onOpen}>
          Open workspace
        </button>
      </td>
    </tr>
  );
}

function NoPortfolio(): ReactNode {
  return (
    <>
      <div className="heading">
        <h1>Offices</h1>
        <p>This account operates no office.</p>
      </div>
      <div className="card">
        <div className="empty">
          <h3>No portfolio</h3>
          <p>
            The agency screens list the offices where you hold an agency operator membership. This
            account holds none. A membership is written by the control plane — an office cannot
            grant one, and neither can an operator who already has one.
          </p>
        </div>
      </div>
    </>
  );
}

function sum(counts: WorkQueue['counts']): number {
  return Object.values(counts).reduce((total, n) => total + n, 0);
}

function Metric({
  label,
  value,
  caption,
}: {
  readonly label: string;
  readonly value: number;
  readonly caption: string;
}): ReactNode {
  return (
    <div className="metric">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      <div className="caption">{caption}</div>
    </div>
  );
}
